import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, TextField, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { tokens } from '../../theme/tokens';
import { DRAFT_TEMPLATES } from '../../constants/draftTemplates';
import { draftDocument } from '../../hooks/useDraftDocument';

const DraftDocumentModal = ({ open, onClose, caseId }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [extraInstructions, setExtraInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setSelectedTemplate(null);
    setExtraInstructions('');
    setDraftText('');
    setError('');
    setCopied(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const draft = await draftDocument(caseId, selectedTemplate.key, extraInstructions);
      setDraftText(draft);
    } catch (err) {
      setError(err.message || 'Error al generar el documento');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(draftText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Redacción con IA
        <IconButton onClick={handleClose} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent>
        {!selectedTemplate ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
            {DRAFT_TEMPLATES.map(tpl => {
              const Icon = tpl.icon;
              return (
                <Box
                  key={tpl.key}
                  onClick={() => setSelectedTemplate(tpl)}
                  sx={{
                    border: `1px solid ${tokens.colors.line}`, borderRadius: `${tokens.radius.md}px`,
                    p: 2, cursor: 'pointer', transition: 'border-color .15s',
                    '&:hover': { borderColor: tokens.colors.indigo },
                  }}
                >
                  <Icon sx={{ color: tokens.colors.indigo, mb: 1 }} />
                  <Typography sx={{ fontWeight: 650, fontSize: 14 }}>{tpl.label}</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.colors.muted }}>{tpl.description}</Typography>
                </Box>
              );
            })}
          </Box>
        ) : !draftText ? (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontWeight: 650, mb: 1 }}>{selectedTemplate.label}</Typography>
            <TextField
              fullWidth multiline minRows={3}
              placeholder="Instrucciones adicionales (opcional): tono, puntos a enfatizar, datos extra…"
              value={extraInstructions}
              onChange={(e) => setExtraInstructions(e.target.value)}
              disabled={generating}
            />
            {error && <Typography sx={{ color: tokens.colors.red, fontSize: 13, mt: 1 }}>{error}</Typography>}
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth multiline minRows={12}
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              sx={{ '& .MuiInputBase-root': { fontSize: 13.5, fontFamily: 'monospace' } }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {selectedTemplate && !draftText && (
          <Button onClick={() => setSelectedTemplate(null)} disabled={generating}>Volver</Button>
        )}
        {draftText && (
          <Button onClick={handleCopy} startIcon={<ContentCopyIcon />} sx={{ color: tokens.colors.cyan }}>
            {copied ? 'Copiado' : 'Copiar'}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {selectedTemplate && !draftText && (
          <Button
            variant="contained" onClick={handleGenerate} disabled={generating}
            sx={{ backgroundImage: tokens.gradient.main, color: '#08131a' }}
          >
            {generating ? <CircularProgress size={18} sx={{ color: '#08131a' }} /> : 'Generar borrador'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DraftDocumentModal;